import { Button, Input, Skeleton, Tooltip } from "antd";
import { SearchOutlined, TeamOutlined, RightOutlined } from "@ant-design/icons";
import ReusableTable from "../../Reuseable/ReusableTable/ReusableTable";
import PanelCard from "../DashboardPage/components/PanelCard";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchTelecallerFieldAgents } from "../../store/telecaller/cases";
import { useNavigate } from "react-router-dom";
import "./FieldAgentAllocation.scss";

const EMPTY = "—";

/* FIELD_AGENT reads as a database constant; the table shows it as words. */
const humanizeRole = (role) =>
  String(role || "")
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const initialsOf = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

const FieldAgentAllocation = () => {
    const { telecaller_field_agents, telecaller_field_agents_loading } = useSelector(state => state.telecallerCases)
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const loading = telecaller_field_agents_loading;
     const [params, setParams] = useState({
        search: ''
    })
    useEffect(() => {
        dispatch(fetchTelecallerFieldAgents(params))
    }, [dispatch,params])

    const rows = useMemo(
      () => (Array.isArray(telecaller_field_agents?.data) ? telecaller_field_agents.data : []),
      [telecaller_field_agents]
    );

    /* Skeleton rows need their own keys - Array(5).fill({}) gave every
       placeholder row the same (undefined) id. */
    const skeletonData = useMemo(
      () => Array(5).fill({}).map((_, i) => ({ id: `skeleton-${i}` })),
      []
    );

    const handleViewClick = (record) => {
navigate(`/allocated-cases/${record.id}`)
    }
    const column = [
        {
            title: 'Full Name',
            dataIndex: 'name',
            key: 'name',
            render: (text) =>
              loading ? (
                <Skeleton.Input size="small" active />
              ) : (
                <span className="faAgent">
                  <span className="faAgent__avatar" aria-hidden="true">
                    {initialsOf(text) || "NA"}
                  </span>
                  <span className="faAgent__name">{text || EMPTY}</span>
                </span>
              ),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (text) =>
              loading ? (
                <Skeleton.Input size="small" active />
              ) : text ? (
                <span className="faEmail">{text}</span>
              ) : (
                <span className="faEmpty">{EMPTY}</span>
              ),
        },
        {
            title: 'Role',
            dataIndex: 'role',
            key: 'role',
            render: (text) =>
              loading ? (
                <Skeleton.Input size="small" active />
              ) : text ? (
                <span className="faRole">{humanizeRole(text)}</span>
              ) : (
                <span className="faEmpty">{EMPTY}</span>
              ),
        },
        {
            title: 'Allocations',
            dataIndex: 'allocated_count',
            key: 'allocated_count',
            align: 'center',
            width: 130,
            render: (text) => {
              if (loading) return <Skeleton.Input size="small" active />;
              const count = Number(text);
              if (!Number.isFinite(count)) {
                return <span className="faEmpty">{EMPTY}</span>;
              }
              return (
                <span className={`faCount ${count > 0 ? "is-some" : "is-none"}`}>
                  {count}
                </span>
              );
            },
        },
        {
            /* was "Ations" */
            title: 'Actions',
            key: 'actions',
            align: 'center',
            width: 150,
            render: (_, record) => {
              if (loading) return <Skeleton.Input size="small" active />;
              return (
                <Tooltip title="See the cases allocated to this agent">
                  <Button
                    className="faViewBtn"
                    onClick={() => handleViewClick(record)}
                  >
                    View Details
                    <RightOutlined />
                  </Button>
                </Tooltip>
              );
            },
        },
    ]

    return (
        <div className="fieldAgentAllocationPage">
            <PanelCard
              title="Field Agent Allocations"
              subtitle={
                loading
                  ? "Loading agents…"
                  : `${rows.length} field agent${rows.length === 1 ? "" : "s"}`
              }
              icon={<TeamOutlined />}
              accent="brand"
              padded={false}
              actions={
                <Input
                    placeholder="Search agents"
                    allowClear
                    className="faSearchInput"
                    prefix={<SearchOutlined />}
                    onPressEnter={(e) => {
                      const value = e.target.value;
                      setParams((prev) => ({
                        ...prev,
                        search: value,
                      }));
                    }}
                    onClear={()=>{
                      setParams((prev)=>({
                          ...prev,
                          search:''
                      }))
                    }}
                />
              }
            >
              <div className="field-agent-report-table">
                <ReusableTable
                    columns={column}
                    dataSource={loading ? skeletonData : rows}
                    rowKey="id"
                    /* the footer read "Showing 0 - 0 of 0 items" over a full
                       table because no total was ever passed */
                    totalItems={rows.length}
                />
              </div>
            </PanelCard>
        </div>
    );
};

export default FieldAgentAllocation;
